import React from 'react';
import Button from '../ui/Button';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { submitContactForm } from '../../features/contact/thunks/contact.thunk';
import { resetContactState } from '../../features/contact/slices/contact.slice';

type ProductInterestFormProps = {
  subject: string;
  ctaLabel: string;
};

const ProductInterestForm: React.FC<ProductInterestFormProps> = ({ subject, ctaLabel }) => {
  const dispatch = useAppDispatch();
  const { isSubmitting } = useAppSelector((state) => state.contact);

  const [form, setForm] = React.useState({ name: '', email: '' });
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    return () => {
      dispatch(resetContactState());
    };
  }, [dispatch]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim()) {
      setError('Please enter your name and email address.');
      return;
    }

    const result = await dispatch(
      submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        subject,
        message: `Interest in ${subject}. Product waitlist / interest request.`,
      })
    );

    if (submitContactForm.fulfilled.match(result)) {
      setSubmitted(true);
      setForm({ name: '', email: '' });
    }
  };

  if (submitted) {
    return (
      <div className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 font-description text-[14px] text-green-700">
        Thank you! We&apos;ve received your interest and will be in touch with updates.
      </div>
    );
  }

  return (
    <form className="mt-4 space-y-3" onSubmit={onSubmit} noValidate>
      <input
        name="name"
        value={form.name}
        onChange={onChange}
        placeholder="Full name"
        className="h-11 w-full rounded-[8px] border border-[#E4E4E4] bg-white px-3 font-description text-[14px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none"
      />
      <input
        name="email"
        type="email"
        value={form.email}
        onChange={onChange}
        placeholder="Email address"
        className="h-11 w-full rounded-[8px] border border-[#E4E4E4] bg-white px-3 font-description text-[14px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none"
      />
      {error && <p className="font-description text-[12px] text-red-500">{error}</p>}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-title text-[13px] font-medium text-white transition-colors hover:bg-[#1a5cc7] disabled:cursor-not-allowed disabled:bg-[#E4E4E4]"
      >
        {isSubmitting ? 'Submitting...' : ctaLabel}
      </Button>
    </form>
  );
};

export default ProductInterestForm;
